import type { Engagement } from '@/entities/discovery';
import { CREATOR_FILTER_DATE_RANGE } from '@/entities/discovery';
import { platformOptions, SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/constants';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/shared/components/shadcn/select';
import { Button } from '@/shared/components/shadcn/button';
import { Slider } from '@/shared/components/shadcn/slider';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/shadcn/tabs';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { cn } from '@/shared/utils';
import { SelectValue } from '@radix-ui/react-select';
import { MAX_ER, MAX_FOLLOWER, MIN_ER, MIN_FOLLOWER } from '../../constants';
import type { FilterConditions } from '@/entities/discovery';
import {
  DATE_RANGE_CONFIG,
  parseDateRangeEnum,
  parseDateRangeToEnum,
} from '@/entities/discovery/utils/creator-date-range-parser';

interface Props {
  filters: FilterConditions;
  disabled?: boolean;
  onFiltersChange: (filters: Partial<FilterConditions>) => void;
  close?: () => void;
  applyLabel?: string;
  className?: string;
}

export function CreatorsInlineFilters({
  filters,
  disabled,
  onFiltersChange,
  close,
  applyLabel = 'Apply',
  className,
}: Props) {
  const followerMin = filters.engagement?.followers_min || MIN_FOLLOWER;
  const followerMax = filters.engagement?.followers_max || MAX_FOLLOWER;
  const erMin = filters.engagement?.er_min || MIN_ER;
  const erMax = filters.engagement?.er_max || MAX_ER;

  const computedDateRangeEnum: CREATOR_FILTER_DATE_RANGE = parseDateRangeToEnum(
    { start_date: filters.start_date, end_date: filters.end_date },
    CREATOR_FILTER_DATE_RANGE.LAST_60_DAYS,
  );

  const handlePlatformToggle = (platform: SOURCE_PLATFORM_TYPE) => {
    onFiltersChange({
      platform: [platform],
    });
  };

  const handleDateRangeChange = (dateRangeEnum: CREATOR_FILTER_DATE_RANGE) => {
    const dateRange = parseDateRangeEnum(dateRangeEnum);
    onFiltersChange({
      start_date: dateRange.start_date,
      end_date: dateRange.end_date,
    });
  };

  const onEngagementChange = (engagement: Partial<Engagement>) => {
    onFiltersChange({
      engagement: {
        ...filters.engagement,
        ...engagement,
      },
    });
  };

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div className="flex flex-col gap-2">
        <p className="text-muted-foreground text-xs font-medium">Platform</p>
        <Tabs
          value={filters?.platform?.[0] ?? undefined}
          onValueChange={(value) => handlePlatformToggle(value as SOURCE_PLATFORM_TYPE)}
        >
          <TabsList className="h-9 w-full">
            {platformOptions.map((option) => {
              const Icon = option.icon || (() => null);

              return option.creator_search_disabled ? (
                <TooltipFlat key={option.value} asChild content="Coming soon">
                  <span className="h-full flex-1">
                    <TabsTrigger value={option.value} disabled className="h-full w-full">
                      <Icon className="size-4" />
                    </TabsTrigger>
                  </span>
                </TooltipFlat>
              ) : (
                <TabsTrigger
                  key={option.value}
                  value={option.value}
                  className="h-full flex-1"
                  disabled={disabled}
                >
                  <Icon className="size-4" />
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-muted-foreground text-xs font-medium">Time range</p>
        <Select
          value={computedDateRangeEnum}
          onValueChange={handleDateRangeChange}
          disabled={disabled}
        >
          <SelectTrigger className="bg-background w-full">
            <SelectValue placeholder="Select time range" />
          </SelectTrigger>
          <SelectContent>
            {DATE_RANGE_CONFIG.toArray().map((config) => (
              <SelectItem key={config.key} value={config.key}>
                {config.value.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-muted-foreground text-xs font-medium">Followers</p>
          <span className="text-muted-foreground text-xs">
            {followerMin / 1000}K - {followerMax / 1000}K
          </span>
        </div>
        <div className="bg-background rounded-xl px-3 py-4">
          <Slider
            min={MIN_FOLLOWER}
            max={MAX_FOLLOWER}
            step={10000}
            value={[followerMin, followerMax]}
            onValueChange={(value) => {
              onEngagementChange({
                followers_min: value[0],
                followers_max: value[1],
              });
            }}
            disabled={disabled}
            className="w-full"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-muted-foreground text-xs font-medium">Average ER</p>
          <span className="text-muted-foreground text-xs">
            {erMin}% - {erMax}%{erMax === MAX_ER ? '+' : ''}
          </span>
        </div>
        <div className="bg-background rounded-xl px-3 py-4">
          <Slider
            min={MIN_ER}
            max={MAX_ER}
            step={1}
            value={[erMin, erMax]}
            onValueChange={(value) => {
              onEngagementChange({
                er_min: value[0],
                er_max: value[1] === MAX_ER ? null : value[1],
              });
            }}
            disabled={disabled}
            className="w-full"
          />
        </div>
      </div>

      <Button onClick={close} disabled={disabled} className="w-full">
        {applyLabel}
      </Button>
    </div>
  );
}
